import { Task } from "@/bryntum-lib/models/Task";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Client } from "@/bryntum-lib/models/Client";

export type EntityRecord = Task | Resource | Client;

/**
 * WidgetParamKeys - Centralized registry for widget action parameter keys
 *
 * Two-tier system:
 * - CREATE actions: Transient params (in-memory only), never persisted to URL
 * - EDIT actions: URL-persisted params (simple IDs), for deep linking
 */

export const WidgetParamKeys = {
  PLANNING: {
    CREATE: 'createPlanning', // transient only
    EDIT: 'editPlanning' // URL-persisted
  },
  REMINDER: {
    CREATE: 'createReminder', // transient only
    EDIT: 'editReminder' // URL-persisted
  },
  NOTE: {
    EDIT: 'editNote' // URL-persisted
  },
  CHECKLIST: {
    EDIT: 'editChecklist' // URL-persisted
  },
  FILE: {
    EDIT: 'editFile' // URL-persisted
  },
  CONTACT: {
    EDIT: 'editContact' // URL-persisted
  },
  LEADS: {
    EDIT: 'submissionId' // URL-persisted
  },
} as const;
// Extract all possible widget param key values as a union type

export type WidgetParamKey = typeof WidgetParamKeys.PLANNING.CREATE |
  typeof WidgetParamKeys.PLANNING.EDIT |
  typeof WidgetParamKeys.REMINDER.CREATE |
  typeof WidgetParamKeys.REMINDER.EDIT |
  typeof WidgetParamKeys.NOTE.EDIT |
  typeof WidgetParamKeys.CHECKLIST.EDIT |
  typeof WidgetParamKeys.FILE.EDIT |
  typeof WidgetParamKeys.CONTACT.EDIT |
  typeof WidgetParamKeys.LEADS.EDIT;

/**
 * EntityManagerOtherParams - Type definition for widget action parameters
 *
 * Usage:
 * - Widgets register handlers for these keys using registerParamAction()
 * - CREATE actions contain complex data (transient, not in URL)
 * - EDIT actions contain simple IDs (persisted to URL for shareability)
 */

export interface EntityManagerOtherParams {
  // CREATE actions (transient, never in URL)
  [WidgetParamKeys.PLANNING.CREATE]?: {
    startDate: Date;
    endDate: Date;
  };
  [WidgetParamKeys.REMINDER.CREATE]?: boolean;

  // EDIT actions (URL-persisted, just IDs for deep linking)
  [WidgetParamKeys.PLANNING.EDIT]?: number;
  [WidgetParamKeys.REMINDER.EDIT]?: number;
  [WidgetParamKeys.NOTE.EDIT]?: number;
  [WidgetParamKeys.CHECKLIST.EDIT]?: number;
  [WidgetParamKeys.FILE.EDIT]?: number | string;
  [WidgetParamKeys.CONTACT.EDIT]?: number | string;
  [WidgetParamKeys.LEADS.EDIT]?: number;
}
